import {useState, useCallback, useMemo} from 'react'
import type {
  CrosstabsClassificationData,
  CrosstabsObjectivesData,
  CrosstabsPlanData,
  CrosstabsProgressData,
  CrosstabsOptionsData,
  CrosstabsFileRequestData
} from './types'

export type CrosstabsTab = 'plan' | 'objectives' | 'variables'

export interface CrosstabsState {
  classification: CrosstabsClassificationData | null
  objectives: CrosstabsObjectivesData | null
  plan: CrosstabsPlanData | null
  progress: CrosstabsProgressData | null
  activeOptions: CrosstabsOptionsData | null
  activeFileRequest: CrosstabsFileRequestData | null
  activeObjectives: CrosstabsObjectivesData | null
  isPanelOpen: boolean
  activePanelTab: CrosstabsTab
}

export interface CrosstabsCallbacks {
  onCrosstabsProgress: (data: CrosstabsProgressData) => void
  onCrosstabsClassification: (data: CrosstabsClassificationData) => void
  onCrosstabsObjectives: (data: CrosstabsObjectivesData) => void
  onCrosstabsPlan: (data: CrosstabsPlanData) => void
  onCrosstabsOptions: (data: CrosstabsOptionsData) => void
  onCrosstabsFileRequest: (data: CrosstabsFileRequestData) => void
}

const INITIAL_STATE: CrosstabsState = {
  classification: null,
  objectives: null,
  plan: null,
  progress: null,
  activeOptions: null,
  activeFileRequest: null,
  activeObjectives: null,
  isPanelOpen: false,
  activePanelTab: 'variables'
}

export function useCrosstabsState(onPanelOpen?: (tab: CrosstabsTab) => void) {
  const [state, setState] = useState<CrosstabsState>(INITIAL_STATE)

  const onCrosstabsProgress = useCallback((data: CrosstabsProgressData) => {
    setState(prev => ({...prev, progress: data.status === 'done' ? null : data}))
  }, [])

  const onCrosstabsClassification = useCallback((data: CrosstabsClassificationData) => {
    // Normalize LLM output to match expected types:
    // - options: string[] → {code, label}[]
    // - type: "SINGLE CHOICE" → "single_choice"
    // - ensure gridGroups/excluded arrays exist
    const normalized: CrosstabsClassificationData = {
      ...data,
      variables: (data.variables ?? []).map(v => ({
        ...v,
        type: v.type?.toLowerCase().replace(/\s+/g, '_') as CrosstabsClassificationData['variables'][0]['type'],
        options: v.options?.map(opt =>
          typeof opt === 'string' ? {code: opt, label: opt} : opt
        ),
        gridScale: v.gridScale?.map((s: any) =>
          typeof s === 'string' ? {code: s, label: s} : s
        ),
        columns: v.columns ?? [],
      })),
      gridGroups: data.gridGroups ?? [],
      excluded: data.excluded ?? []
    }
    setState(prev => ({
      ...prev,
      classification: normalized,
      isPanelOpen: true,
      activePanelTab: 'variables'
    }))
    onPanelOpen?.('variables')
  }, [onPanelOpen])

  const onCrosstabsObjectives = useCallback((data: CrosstabsObjectivesData) => {
    // Normalize LLM output to match expected types
    const normalized: CrosstabsObjectivesData = {
      ...data,
      coreBanners: (data.coreBanners ?? []).map(b => ({
        ...b,
        options: b.options ?? [],
        nets: (b.nets ?? []).map(n => ({...n, options: n.options ?? []}))
      })),
      objectives: (data.objectives ?? []).map(o => ({
        ...o,
        stubs: (o.stubs ?? []).map(s => ({
          variable: s.variable ?? '',
          label: s.label ?? '',
          tableType: s.tableType ?? 'frequency'
        })),
        supplementaryBanners: o.supplementaryBanners ?? []
      })),
      noneOptionLabel: data.noneOptionLabel ?? 'None of the above'
    }
    setState(prev => ({
      ...prev,
      objectives: normalized,
      activeObjectives: normalized
    }))
  }, [])

  const onCrosstabsPlan = useCallback((data: CrosstabsPlanData) => {
    // Normalize LLM output to match expected types
    const normalized: CrosstabsPlanData = {
      ...data,
      objectives: data.objectives ?? '',
      crosstabs: (data.crosstabs ?? []).map(ct => ({
        ...ct,
        banners: (ct.banners ?? []).map(b => ({
          ...b,
          options: b.options?.map((o: any) =>
            typeof o === 'string' ? {code: o, label: o} : o
          )
        })),
        stubs: (ct.stubs ?? []).map(s => ({
          ...s,
          // LLM might use "tableType" instead of "table_type"
          table_type: s.table_type ?? (s as any).tableType ?? 'frequency',
          nets: (s.nets ?? []).map(n => ({
            ...n,
            kind: n.kind ?? 'choice' as const,
            options: n.options ?? []
          }))
        })),
        settings: ct.settings ?? {}
      }))
    }
    setState(prev => ({
      ...prev,
      plan: normalized,
      isPanelOpen: true,
      activePanelTab: 'plan'
    }))
    onPanelOpen?.('plan')
  }, [onPanelOpen])

  const onCrosstabsOptions = useCallback((data: CrosstabsOptionsData) => {
    setState(prev => ({...prev, activeOptions: data}))
  }, [])

  const onCrosstabsFileRequest = useCallback((data: CrosstabsFileRequestData) => {
    setState(prev => ({...prev, activeFileRequest: data}))
  }, [])

  // Memoized: this object sits in R3Chat's handleSend dependency array. As a per-render literal it
  // minted a new handleSend (→ new onSend/onApprovePlan props) on EVERY R3Chat render, silently
  // defeating MessageList's memo — the whole thread re-rendered through react-markdown whenever
  // anything ticked (typing before, the 60fps voice level during recording). Members are all
  // stable useCallbacks, so this is stable forever.
  const callbacks: CrosstabsCallbacks = useMemo(() => ({
    onCrosstabsProgress,
    onCrosstabsClassification,
    onCrosstabsObjectives,
    onCrosstabsPlan,
    onCrosstabsOptions,
    onCrosstabsFileRequest
  }), [onCrosstabsProgress, onCrosstabsClassification, onCrosstabsObjectives, onCrosstabsPlan, onCrosstabsOptions, onCrosstabsFileRequest])

  const openPanel = useCallback((tab?: CrosstabsTab) => {
    setState(prev => ({
      ...prev,
      isPanelOpen: true,
      activePanelTab: tab ?? prev.activePanelTab
    }))
  }, [])

  const closePanel = useCallback(() => {
    setState(prev => ({...prev, isPanelOpen: false}))
  }, [])

  const dismissOptions = useCallback(() => {
    setState(prev => ({...prev, activeOptions: null}))
  }, [])

  const dismissFileRequest = useCallback(() => {
    setState(prev => ({...prev, activeFileRequest: null}))
  }, [])

  const dismissObjectives = useCallback(() => {
    setState(prev => ({...prev, activeObjectives: null}))
  }, [])

  const reset = useCallback(() => {
    setState(INITIAL_STATE)
  }, [])

  // Clear the live compute-progress line when a turn ends, so a stale "Computing… (Ns)"
  // can't linger into the next turn.
  const clearProgress = useCallback(() => {
    setState(prev => (prev.progress ? {...prev, progress: null} : prev))
  }, [])

  return {
    state,
    callbacks,
    openPanel,
    closePanel,
    dismissOptions,
    dismissFileRequest,
    dismissObjectives,
    clearProgress,
    reset
  }
}
